import template from './metric-approvals-preview.hbs'
import metricApprovalsHbs from './metric-approvals.hbs'
import BaseMetricApprovals from './base-metric-approvals'
import i18n from '../../components/i18n'
import _ from 'lodash'
import DetailsTooltip from './details-tooltip'
import EquationValueTooltips from '../../scorecards/equation-value-tooltips'
import calendars from '../../components/calendars'
import TooltipAndDetails from 'scoreboard/components/tooltip-and-details'
import constants from 'scoreboard/components/constants'
import $ from 'jquery'
import Breadcrumbs from 'scoreboard/components/breadcrumbs'
import { SCORECARDS, ORGANIZATIONS } from 'scoreboard/components/sections'

const MetricApprovalsPreview = BaseMetricApprovals.extend(/** @lends {MetricApprovalsPreview} */{

	className: 'metric-approvals metric-approvals-preview',

	/**
	 * The preview of the metric approvals shown in a dialog when the user
	 * clicks on the "Preview Results" button on the "Metric Approvals" screen
	 * (#55590)
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Object[]} options.data the data to display in the preview (an
	 * array of MetricChangeMetadata.kt objects)
	 *
	 * @param {Number} options.numAffectedMetrics The number of metrics that
	 * are affected by the changes
	 *
	 * @param {Number} options.numViewableMetrics The number of metrics that
	 * are viewable in the preview
	 *
	 * @param {Array} options.manualMetrics the manual metrics data (an array
	 * of MetricChangeMetadata.kt objects)
	 *
	 * @param {Object} options.seriesBySnId metadata about the series for the
	 * metrics mentioned in the preview
	 */
	initialize: function(options = {}) {
		BaseMetricApprovals.prototype.initialize.call(this, options)
		this.manualMetrics = options.manualMetrics || []

		this.initializeManualMetricsByIdAndPeriod()
	},

	initializeManualMetricsByIdAndPeriod: function() {
		this.manualMetricsByIdAndPeriod = {}
		this.manualMetrics.forEach(metric => {
			if (!this.manualMetricsByIdAndPeriod[metric.scorecardNodeId]) {
				this.manualMetricsByIdAndPeriod[metric.scorecardNodeId] = {}
			}
			this.manualMetricsByIdAndPeriod[metric.scorecardNodeId][metric.calendarPeriodId] = metric
		})
	},

	render: function() {
		this.$el.empty().append(template(this.options))
		this.$('.dialog-body-content').append(metricApprovalsHbs())
		this.$('.details').html(i18n.get('common.details'))
		this.$('footer').remove()
		this.renderBanner()
		this.renderMetricsTable(this.data)
		this.setupPendingUpdatesTooltips()
		this.setupEquationTooltips()
		this.setupCascadeTooltipsForReferencedMetrics()
		return this
	},

	renderBanner: function() {
		if (this.options.numAffectedMetrics == this.options.numViewableMetrics) {
			this.$('.banner').html(i18n.countMessage(
				'home.approveMetrics.previewResults.banner.viewAll',
				this.options.numAffectedMetrics))
		} else {
			let numHidden = this.options.numAffectedMetrics - this.options.numViewableMetrics
			this.$('.banner').html(i18n.countMessage(
				'home.approveMetrics.previewResults.banner.viewSome',
				this.options.numViewableMetrics,
				this.options.numViewableMetrics, numHidden))
		}
	},

	/**
	 * Overrides the renderRow method to include the pending updates column
	 * @param {Object} row - The row data
	 * @param {jQuery} tbody - The table body element
	 */
	renderRow: function(row, tbody) {
		row.referencedMetrics = this.buildReferencedMetrics(row)
		BaseMetricApprovals.prototype.renderRow.call(this, row, tbody)
		this.emphasizeNewValue(row)
	},

	/**
	 * Sets up cascade tooltips for each referenced metric in the row (#61360)
	 */
	setupCascadeTooltipsForReferencedMetrics: function(row) {
		this.cascadeTooltipAndDetails = new TooltipAndDetails({
			selector: `.icon-container[data-type="referenced-metric"]`,
			tooltipContent: (e) => {
				let orgId = $(e.currentTarget)
					.closest('[data-organizationid]')
					.data('organizationid')
				let metricId = $(e.currentTarget)
					.closest('[data-metric-id]')
					.data('metric-id')
				// retrieve ancestors for the org and the scorecard in parallel
				return Promise.all([
					$.get(`api/${ORGANIZATIONS}/${orgId}/ancestors`),
					$.get(`api/${SCORECARDS}/${metricId}/ancestors`)
				]).then(([orgAncestors, scorecardAncestors]) => {
					const orgBreadcrumbs = Breadcrumbs.getBreadcrumbs({
						objects: orgAncestors
					}).render()
					const scorecardBreadcrumbs = Breadcrumbs.getBreadcrumbs({
						objects: scorecardAncestors
					}).render()

					let content =
						`<div class="breadcrumbs">` +
						`${orgBreadcrumbs.$el.html()}</div>` +
						`<div class="breadcrumbs">` +
						`${scorecardBreadcrumbs.$el.html()}</div>`
					return content
				})
			},
			tooltipOptions: {
				extraClass: 'breadcrumbs-tooltip',
				setTooltipBodyMaxHeight: true
			},
			el: this.el
		})
	},

	/**
	 * Adds text-link-emphasized class to the numbers in the "New Values" column
	 * @param {Object} row - The row data containing new values
	 */
	emphasizeNewValue: function(row) {
		const addEmphasis = (valueKey, columnClass) => {
			if (row[valueKey]) {
				const selector = this.getRowSelector(row) +
					`td.${columnClass} strong`
				this.$(selector).addClass('text-link-emphasized')
			}
		}

		addEmphasis('newValues', 'new-values')
		addEmphasis('oldValues', 'old-values')
	},

	/**
	 * Builds a sorted list of referenced metrics for a given row
	 * @param {Object} row - The row data containing references
	 * @returns {Array} Sorted array of referenced metrics with no duplicates
	 */
	buildReferencedMetrics: function(row) {
		const referencedMetrics = []

		// references is a map of periodId -> Set<ISnAndPeriodId>
		if (row.references) {
			// Flatten all referenced metrics across all periods
			Object.values(row.references).forEach(snAndPeriodIds => {
				snAndPeriodIds.forEach(reference => {
					const metric = this.manualMetricsByIdAndPeriod[reference.scorecardNodeId]?.[reference.calendarPeriodId]
					if (metric) {
						referencedMetrics.push({
							...metric,
							calendarPeriodId: reference.calendarPeriodId
						})
					}
				})
			})
		}

		// Remove duplicates and sort by metric label (case-insensitive) and
		// then by period end date
		return _.uniqBy(
			_.sortBy(referencedMetrics, [
				metric => metric.scorecardNodeLabel.toLowerCase(),
				metric => calendars.periodsById[metric.calendarPeriodId]?.end.valueOf()
			]),
			metric => `${metric.scorecardNodeId}-${metric.calendarPeriodId}`
		)
	},

	/** Sets up the tooltips for the "Pending Updates" column */
	setupPendingUpdatesTooltips: function() {
		this.puDetailsTooltips = []
		_.each(this.$('.pending-updates svg.icon-info'), el => {
			const { metricId, periodId } = this.$(el).closest('[data-metric-id]').data()

			// Get the metric data for this specific metric+period combination
			const data = this.manualMetricsByIdAndPeriod[metricId]?.[periodId]
			if (data) {
				let period = calendars.periodsById[periodId]
				let title = i18n.get('home.approveMetrics.previewResults.tooltip.title',
					period.label, data.updateUserLabel ?? '')
				this.puDetailsTooltips.push(new DetailsTooltip({
					data,
					title,
					attachTo: el
				}))
			}
		})
	},

	setupEquationTooltips: function() {
		// this is a bit of a hack since we potentially have a copy of the
		// metric for each calendar period, but the key information we care
		// about for the metrics doesn't vary by period
		let metrics = _.map(this.options.data, metric => ({
			...metric,
			id: metric.scorecardNodeId,
			series: this.options.seriesBySnId[metric.scorecardNodeId]
		}))
		this.oldEquationValueTooltips = new EquationValueTooltips({
			metrics,
			el: this.$('.old-values'),
		}).render()
		this.newEquationValueTooltips = new EquationValueTooltips({
			metrics,
			el: this.$('.new-values'),
			proposedUpdates: this.options.manualMetrics
		}).render()
	},

	remove: function() {
		BaseMetricApprovals.prototype.remove.apply(this, arguments)
		_.invokeMap([
			this.newEquationValueTooltips,
			this.oldEquationValueTooltips,
			this.cascadeTooltipAndDetails
		], 'remove')
		_.invokeMap(this.puDetailsTooltips, 'remove')
	}

})

export default MetricApprovalsPreview

MetricApprovalsPreview.__filename__ = 'home/metric-approvals/metric-approvals-preview.js'
